/* eslint-disable lit-a11y/click-events-have-key-events */
import { LitElement, html, css } from "lit";
import { customElement, state, property } from "lit/decorators.js";
import { when } from "lit/directives/when.js";
import { debounce, AppClassToggle } from "../functions";
import { menu, close } from "../svg";
import type { Category } from "./entities";

const MENU_BREAKPOINT = 992;

@customElement("umb-market-categories")
export class UmbMarketCategories extends LitElement {
  @property({ type: Array })
  categories?: Array<Category> = [];

  @state()
  private _open!: boolean;

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener("resize", this.#debouncedResize);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener("resize", this.#debouncedResize);
  }

  #handleResize = () => {
    if (document.body.clientWidth >= MENU_BREAKPOINT) {
      this.#close();
    }
  };

  #debouncedResize = debounce(this.#handleResize);

  #close() {
    this._open = false;
    AppClassToggle("categories-in", "remove");
  }

  #toggleMenu() {
    this._open = !this._open;
    AppClassToggle("categories-in");
  }

  render() {
    return html`
      <umb-market-button
        aria-label="Open categories menu"
        class="menu-btn"
        id="open-menu-btn"
        @click=${this.#toggleMenu}
      >
        ${menu}
      </umb-market-button>
      <div class="panel ${when(this._open, () => "in")}">
        <umb-market-button
          aria-label="Close categories menu"
          class="menu-btn ${when(!this._open, () => "out")}"
          id="close-menu-btn"
          type="button"
          @click=${this.#toggleMenu}
        >
          ${close}
        </umb-market-button>
        <div class="category-row">
          ${this.categories?.map(
            (category, idx) =>
              html`<umb-market-category
                @click=${this.#close}
                .value=${category}
                tooltip-position=${idx <= ((this.categories?.length ?? 0) - 1) / 2
                  ? "left"
                  : "right"}
              ></umb-market-category>`
          )}
        </div>
      </div>
    `;
  }

  static styles = [
    css`
      .category-row {
        display: flex;
        flex-direction: column;
        max-width: var(--max-width);
        margin: 115px 0 0;
        justify-content: center;
        gap: var(--xs-spacer);
      }

      .panel {
        overflow: hidden;
        height: 0;

        --button-background: var(--uui-color-default);
        --button-color: white;
        --button-hover-color: white;
      }

      .in {
        height: auto;
        position: fixed;
        top: 0;
        bottom: 0;
        left: 0;
        right: 0;
        z-index: 1000;
        overflow-y: scroll;
        background-color: white;
        padding: var(--m-spacer);
      }

      .out {
        display: none !important;
      }

      .menu-btn {
        --button-border-radius: 100px;
        --button-width: var(--btn-round-size);
        --button-height: var(--btn-round-size);

        position: fixed;
        z-index: 5050;
        top: var(--l-spacer);
        right: var(--l-spacer);
        justify-content: center;
        align-items: center;
      }

      #open-menu-btn {
        opacity: var(--open-menu-btn-opacity, 1);
      }

      @media (min-width: 767px) {
        .menu-btn {
          right: var(--xl-spacer);
          top: var(--xl-spacer);
        }
      }

      @media (min-width: 992px) {
        .panel {
          height: auto;
          overflow: unset;
        }

        .category-row {
          margin: calc(2 * var(--m-spacer)) auto 0;
          flex-direction: row;
          flex-wrap: wrap;
          gap: var(--s-spacer);
          max-width: 780px;
        }

        .menu-btn {
          display: none;
        }
      }

      @media (min-width: 1450px) {
        .category-row {
          max-width: 1000px;
        }
      }

      @media (min-width: 1700px) {
        .category-row {
          max-width: none;
        }
      }
    `,
  ];
}
